'use client';
import { imageSource, resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { VariantsEntity } from '@eliadmin/core/src/api/products/types/product-types';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import type { ProductDetailsWithSlug } from '@eliadmin/core/src/stores/variant-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { lazy as lazyProductLightbox, Suspense as ProductLightboxBoundary } from 'react';

const LightboxComponent = lazyProductLightbox(() =>
  import('@eliadmin/core/src/components/lightbox/lightbox-component').then(module => ({
    default: module.LightboxComponent,
  })),
);

export function ProductImages() {
  const templateSettings = useGlobalStore(state => state.templateSettings);
  const product = useVariantsStore(s => s.product);
  const selectedVariant = useVariantsStore(state => state.selectedVariant);

  const images = filerImages(
    templateSettings.product_detail?.gallery_with_image_variants?.enabled ?? false,
    product,
    selectedVariant,
  );

  const visibleImages = images.slice(0, 3);
  const mainImages =
    visibleImages.map((image: any, i) => (
      <div
        key={image.id ?? image.url ?? i}
        className={cn(
          'flex h-full max-h-[600px] w-full items-center justify-center rounded-lg bg-card',
          'ea-product-detail-images__carousel-slide',
        )}
      >
        <Image
          alt={resolveImageAlt(image, product?.name)}
          src={image.url}
          width={600}
          height={600}
          sizes="(min-width: 1280px) 620px, (min-width: 768px) 50vw, calc(100vw - 32px)"
          priority={i === 0}
          fetchPriority={i === 0 ? 'high' : 'auto'}
          className={cn(
            'h-full max-h-[600px] w-full rounded-lg object-contain',
            'ea-product-detail-images__carousel-slide-image',
          )}
        />
      </div>
    )) ?? [];

  const thumbnailImages = visibleImages.map((image: any, i) => (
    <Image
      key={image.id ?? image.url ?? i}
      alt={resolveImageAlt(image, product?.name)}
      src={image.url}
      width={160}
      height={160}
      sizes="160px"
      loading="lazy"
      className="h-full w-full rounded-lg object-contain"
    />
  ));

  const { isOpen, setIsOpen } = useDisclosure();

  return (
    <div
      className={cn(
        'flex w-full flex-col items-start justify-start gap-4',
        'ea-product-detail-images',
      )}
    >
      {isOpen && (
        <ProductLightboxBoundary fallback={null}>
          <LightboxComponent
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            images={images}
            zoom={{ maxZoomPixelRatio: 8 }}
          />
        </ProductLightboxBoundary>
      )}
      <Carousel
        showThumbnails
        options={{ loop: true }}
        className={{
          main: cn(
            'aspect-square max-h-[600px] w-full overflow-hidden rounded-[8px] border sm:w-full',
            'ea-product-detail-images__carousel-wrapper',
          ),
          slide: cn('rounded-[8px]', 'ea-product-detail-images__carousel-slide'),
          container: cn('', 'ea-product-detail-images__carousel-container'),
          thumbnail: cn(
            'col-span-1 aspect-square w-full rounded-[8px]',
            'ea-product-detail-images__carousel-thumbnails',
          ),
          thumbnailsWrapper: cn(
            'grid w-full grid-cols-4 gap-4',
            'ea-product-detail-images__carousel-thumbnails',
          ),
          thumbnails: cn(
            'col-span-1 rounded-[8px] border border-border',
            'ea-product-detail-images__carousel-thumbnails',
          ),
        }}
        slides={mainImages}
        thumbnailSlides={thumbnailImages}
        openLightbox={() => setIsOpen(true)}
        showMoreButton={images.length > 3}
        allImagesCount={images.length}
      />
    </div>
  );
}

function filerImages(
  showOnlyVariantImages: boolean,
  product: ProductDetailsWithSlug,
  selectedVariant?: VariantsEntity,
) {
  const variantImages = Array.isArray(selectedVariant?.images) ? selectedVariant.images : [];
  const productImages = Array.isArray(product?.images) ? product.images : [];
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  const images =
    showOnlyVariantImages && variantImages.some(image => imageSource(image))
      ? variantImages
      : [
          product?.image,
          ...productImages,
          ...variants.flatMap(variant => (Array.isArray(variant?.images) ? variant.images : [])),
        ];
  return images.filter((image): image is NonNullable<typeof image> => Boolean(imageSource(image)));
}
